
import { ThumbsUp, ThumbsDown, Plus, ChevronDown, ChevronLeft, ChevronRight, Menu, Check, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FeedbackDialog } from './../PopUp/FeedBackDialog';
import { FamlaServices } from '../../../services';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../../services/api/uiButtonStatsService';
import { useAppSelector } from '../../../stores';
import { useTranslation } from 'react-i18next';

interface SwimlaneTabsProps {
    swimlanes: { _id: string; name?: string }[];
    mapId: string;
    setMap: (id: string) => void;
    handleSetMapId: (id: string) => void;
    hasUnsavedChanges: boolean;
    swimlaneName: string;
    handleAction: () => void;
    submit: boolean;
    alert: { msg: string; type: string };
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    isDisabled: boolean;
    onAddSheet?: () => void;
    onRenameSheet?: (sheetId: string, newName: string) => void;
    onDeleteSheet?: (sheetId: string) => void;
    onCopySheet?: (sheetId: string) => void;
    onMoveSheet?: (sheetId: string, direction: 'left' | 'right') => void;
    onToggleSheetVisibility?: (sheetId: string) => void;
    hiddenSheetIds?: string[];
    canManageSheets?: boolean;
    isMapSwitching?: boolean;
    mapSwitchTargetId?: string | null;
}

const SwimlaneTabs = (props: SwimlaneTabsProps) => {
    const {
        swimlanes: originalSwimlanes,
        mapId,
        setMap,
        onAddSheet,
        onRenameSheet,
        onDeleteSheet,
        onCopySheet,
        onMoveSheet,
        onToggleSheetVisibility,
        hiddenSheetIds = [],
        canManageSheets = false,
        isMapSwitching = false,
        mapSwitchTargetId = null,
    } = props;
    const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null);
    const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
    const [openTabMenuId, setOpenTabMenuId] = useState<string | null>(null);
    const [showAllSheetsMenu, setShowAllSheetsMenu] = useState(false);
    const [allSheetsMenuPos, setAllSheetsMenuPos] = useState({ left: 0, top: 0 });
    const [tabMenuPos, setTabMenuPos] = useState({ left: 0, top: 0 });
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const allSheetsPortalRef = useRef<HTMLDivElement | null>(null);
    const tabMenuPortalRef = useRef<HTMLDivElement | null>(null);
    const tabsScrollRef = useRef<HTMLDivElement | null>(null);
    const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const users = useAppSelector((state) => state.auth);
    const { t } = useTranslation();

    const swimlanes = React.useMemo(() => [...originalSwimlanes], [originalSwimlanes]);
    const visibleSwimlanes = React.useMemo(
        () => swimlanes.filter((sheet) => !hiddenSheetIds.includes(sheet._id)),
        [swimlanes, hiddenSheetIds]
    );
    const hiddenSwimlanes = React.useMemo(
        () => swimlanes.filter((sheet) => hiddenSheetIds.includes(sheet._id)),
        [swimlanes, hiddenSheetIds]
    );

    const handleFeedback = async (feedback: string, status: boolean) => {
        try {
            const feedbackData = {
                map_id: mapId,
                thumbs: status,
                content: feedback,
                created_at: new Date().toISOString()
            };

            const data = await FamlaServices("api_host", "/comment/", "POST", feedbackData, users.access_token);

            if (data.status === 201) {
                setShowFeedbackDialog(false);
            }

            return data.body;
        } catch (error) {
            console.error("Erreur lors de l'envoi du commentaire :", error);
            return null;
        }
    };

    const handleTabClick = (newMapId: string) => {
        if (mapId === newMapId) return;
        if (isMapSwitching) return;
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsTabSwitch);
        setMap(newMapId);
    };

    const handleRename = (sheetId: string, currentName?: string) => {
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsRename);
        onRenameSheet?.(sheetId, currentName || '');
        setOpenTabMenuId(null);
    };

    const handleDelete = (sheetId: string) => {
        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsDelete);
        onDeleteSheet?.(sheetId);
        setOpenTabMenuId(null);
    };

    const updateScrollButtons = React.useCallback(() => {
        const el = tabsScrollRef.current;
        if (!el) return;
        const maxScrollLeft = el.scrollWidth - el.clientWidth;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(maxScrollLeft > 1 && el.scrollLeft < maxScrollLeft - 1);
    }, []);

    const scrollTabsBy = (delta: number) => {
        const el = tabsScrollRef.current;
        if (!el) return;
        if (delta < 0) {
            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsScrollLeft);
        } else if (delta > 0) {
            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsScrollRight);
        }
        el.scrollBy({ left: delta, behavior: 'smooth' });
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            const targetElement = event.target as HTMLElement | null;
            if (targetElement?.closest('[data-all-sheets-trigger="true"]')) return;
            if (targetElement?.closest('[data-tab-menu-trigger="true"]')) return;
            if (allSheetsPortalRef.current && allSheetsPortalRef.current.contains(target)) return;
            if (tabMenuPortalRef.current && tabMenuPortalRef.current.contains(target)) return;
            setShowAllSheetsMenu(false);
            setOpenTabMenuId(null);
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowAllSheetsMenu(false);
                setOpenTabMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    useEffect(() => {
        updateScrollButtons();
    }, [visibleSwimlanes, updateScrollButtons]);

    useEffect(() => {
        const el = tabsScrollRef.current;
        if (!el) return;
        updateScrollButtons();

        const onScroll = () => updateScrollButtons();
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, [updateScrollButtons]);

    useEffect(() => {
        const scrollTargetId = (isMapSwitching && mapSwitchTargetId) ? mapSwitchTargetId : mapId;
        const activeTab = tabRefs.current[scrollTargetId];
        if (!activeTab) return;
        activeTab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }, [mapId, mapSwitchTargetId, isMapSwitching, visibleSwimlanes]);

    return (
        <>
            <div className="d-flex justify-content-between bg-[#f1f3f4] relative z-[10000] border-t border-gray-300">
                <div className="flex items-center gap-1 h-full px-2 py-1 overflow-hidden min-h-[42px]">
                    {canManageSheets && (
                        <>
                            <button
                                onClick={() => {
                                    void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsAdd);
                                    onAddSheet?.();
                                }}
                                type="button"
                                className="w-8 h-8 rounded-full text-gray-700 hover:bg-gray-200 flex items-center justify-center shrink-0"
                                title={t('features.swimlane.addSheet')}
                            >
                                <Plus size={16} />
                            </button>
                            <div className="relative">
                            <button
                                className="w-8 h-8 rounded-full text-gray-700 hover:bg-gray-200 flex items-center justify-center shrink-0"
                                onClick={(event) => {
                                    void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsAllSheetsMenu);
                                    const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                    setAllSheetsMenuPos({ left: rect.left, top: rect.top - 8 });
                                    setShowAllSheetsMenu((prev) => !prev);
                                    setOpenTabMenuId(null);
                                }}
                                type="button"
                                data-all-sheets-trigger="true"
                                title={t('features.swimlane.allSheets')}
                            >
                                <Menu size={16} />
                            </button>
                            {showAllSheetsMenu && createPortal(
                                <div
                                    className="fixed z-[20000] bg-white border border-gray-200 rounded shadow-md max-h-[220px] overflow-auto"
                                    style={{
                                        left: `${allSheetsMenuPos.left}px`,
                                        top: `${allSheetsMenuPos.top}px`,
                                        transform: 'translateY(-100%)',
                                        width: 'max-content',
                                        minWidth: '160px',
                                        maxWidth: '240px'
                                    }}
                                    ref={allSheetsPortalRef}
                                >
                                    {visibleSwimlanes.map((sheet) => {
                                        const isPendingTarget =
                                            isMapSwitching && mapSwitchTargetId === sheet._id;
                                        return (
                                        <button
                                            key={`all-visible-${sheet._id}`}
                                            className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap flex items-center justify-between gap-2 ${isPendingTarget ? 'bg-blue-50 text-blue-700' : mapId === sheet._id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 text-gray-800'}`}
                                            onClick={() => {
                                                handleTabClick(sheet._id);
                                                setShowAllSheetsMenu(false);
                                                setOpenTabMenuId(null);
                                            }}
                                            disabled={isMapSwitching && !isPendingTarget}
                                        >
                                            <span className="truncate flex items-center gap-2">
                                                {isPendingTarget && (
                                                    <Loader2 size={14} className="shrink-0 animate-spin" />
                                                )}
                                                {sheet.name || sheet._id}
                                            </span>
                                            {mapId === sheet._id && !isPendingTarget && <Check size={14} className="shrink-0" />}
                                        </button>
                                        );
                                    })}
                                    {hiddenSwimlanes.length > 0 && (
                                        <div className="border-t border-gray-200">
                                            <p className="px-3 py-2 text-xs text-gray-500 uppercase mb-0">
                                                {t('features.swimlane.hiddenSheets')}
                                            </p>
                                            {hiddenSwimlanes.map((sheet) => (
                                                <button
                                                    key={`all-hidden-${sheet._id}`}
                                                    className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap"
                                            onClick={() => {
                                                void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsShowHidden);
                                                onToggleSheetVisibility?.(sheet._id);
                                                setShowAllSheetsMenu(false);
                                            }}
                                                >
                                                    {t('features.swimlane.showSheet')}: {sheet.name || sheet._id}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>,
                                document.body
                            )}
                            </div>
                        </>
                    )}

                    {(canScrollLeft || canScrollRight) && (
                        <>
                            <button
                                type="button"
                                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${canScrollLeft ? 'text-gray-700 hover:bg-gray-200' : 'text-gray-400 cursor-default'}`}
                                onClick={() => scrollTabsBy(-220)}
                                disabled={!canScrollLeft}
                                title={t('words.previous') || 'Previous'}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${canScrollRight ? 'text-gray-700 hover:bg-gray-200' : 'text-gray-400 cursor-default'}`}
                                onClick={() => scrollTabsBy(220)}
                                disabled={!canScrollRight}
                                title={t('words.next') || 'Next'}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </>
                    )}
                    <div className="relative flex-1 min-w-0 ml-1">
                        {canScrollLeft && (
                            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-[#f1f3f4] to-transparent z-10" />
                        )}
                        {canScrollRight && (
                            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-[#f1f3f4] to-transparent z-10" />
                        )}
                        <div
                            ref={tabsScrollRef}
                            className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                            onWheel={(event) => {
                                if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                                    event.preventDefault();
                                    scrollTabsBy(event.deltaY);
                                }
                            }}
                        >
                        {visibleSwimlanes.map((swimLane: any, index: number) => {
                            const isActive = mapId === swimLane._id;
                            const isPendingTarget =
                                isMapSwitching && mapSwitchTargetId === swimLane._id;
                            const tabClass = isPendingTarget
                                ? 'bg-blue-50 text-blue-800 border-blue-400 shadow-[0_0_0_1px_#60a5fa_inset]'
                                : isActive
                                    ? 'bg-white text-blue-700 border-gray-300 shadow-[0_-1px_0_0_#dadce0_inset]'
                                    : isMapSwitching
                                        ? 'text-gray-500 opacity-70'
                                        : 'text-gray-700 hover:bg-gray-200';

                            return (
                            <div
                                key={`${swimLane._id}-${index}`}
                                ref={(el) => { tabRefs.current[swimLane._id] = el; }}
                                className={`h-8 flex items-center whitespace-nowrap px-2 gap-1 rounded-t-md border border-transparent transition-colors duration-150 ${tabClass}`}
                                aria-busy={isPendingTarget}
                            >
                                <button
                                    className="px-1 py-1 h-full flex items-center max-w-[180px] gap-1.5"
                                    onClick={() => {
                                        setShowAllSheetsMenu(false);
                                        setOpenTabMenuId(null);
                                        handleTabClick(swimLane._id);
                                    }}
                                    disabled={isMapSwitching && !isPendingTarget}
                                >
                                    {isPendingTarget && (
                                        <Loader2
                                            size={14}
                                            className="shrink-0 animate-spin text-blue-600"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <span className="truncate block max-w-[150px]">
                                        {swimLane.name || swimLane._id}
                                    </span>
                                </button>
                                {canManageSheets && (
                                    <button
                                        type="button"
                                        className="p-1 rounded-full hover:bg-gray-200 shrink-0"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                            setTabMenuPos({ left: rect.left, top: rect.top - 8 });
                                            setOpenTabMenuId((prev) => prev === swimLane._id ? null : swimLane._id);
                                            setShowAllSheetsMenu(false);
                                        }}
                                        data-tab-menu-trigger="true"
                                        title={t('features.swimlane.allSheets')}
                                    >
                                        <ChevronDown
                                            size={14}
                                            className={`transition-transform duration-150 ${openTabMenuId === swimLane._id ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                )}
                                {canManageSheets && openTabMenuId === swimLane._id && createPortal(
                                    <div
                                        className="fixed z-[20000] bg-white border border-gray-200 rounded shadow-md"
                                        style={{
                                            left: `${tabMenuPos.left}px`,
                                            top: `${tabMenuPos.top}px`,
                                            transform: 'translateY(-100%)',
                                            width: 'max-content',
                                            minWidth: 'unset',
                                            maxWidth: '220px'
                                        }}
                                        ref={tabMenuPortalRef}
                                    >
                                        <button className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap" onClick={() => handleRename(swimLane._id, swimLane.name)}>
                                            {t('words.rename')}
                                        </button>
                                        <button className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap"                                         onClick={() => {
                                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsCopy);
                                            onCopySheet?.(swimLane._id);
                                            setOpenTabMenuId(null);
                                        }}>
                                            {t('features.swimlane.makeCopy')}
                                        </button>
                                        <button className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap"                                         onClick={() => {
                                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsMoveLeft);
                                            onMoveSheet?.(swimLane._id, 'left');
                                            setOpenTabMenuId(null);
                                        }}>
                                            {t('features.swimlane.moveSheetLeft')}
                                        </button>
                                        <button className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap" onClick={() => {
                                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsMoveRight);
                                            onMoveSheet?.(swimLane._id, 'right');
                                            setOpenTabMenuId(null);
                                        }}>
                                            {t('features.swimlane.moveSheetRight')}
                                        </button>
                                        <button className="block text-left px-3 py-2 text-sm hover:bg-gray-100 whitespace-nowrap" onClick={() => {
                                            onToggleSheetVisibility?.(swimLane._id);
                                            setOpenTabMenuId(null);
                                        }}>
                                            {t('features.swimlane.hideSheet')}
                                        </button>
                                        <button className="block text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 whitespace-nowrap" onClick={() => handleDelete(swimLane._id)}>
                                            {t('words.delete')}
                                        </button>
                                    </div>,
                                    document.body
                                )}
                            </div>
                            );
                        })}
                        </div>
                    </div>
                </div>

                {/* Feedback System */}
                <div className="flex items-center gap-2 px-4">
                    <p className="text-gray-600">{t('features.swimlane.helpful')}</p>
                    <button
                        onClick={() => {
                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsFeedbackThumbsUp);
                            setFeedbackStatus('positive');
                            handleFeedback('', true);
                        }}
                        className={`p-2 rounded-full transition-colors ${feedbackStatus === 'positive' ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 text-gray-500'
                            }`}
                        aria-label="Thumbs up"
                    >
                        <ThumbsUp size={20} />
                    </button>
                    <button
                        onClick={() => {
                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.sheetsFeedbackThumbsDown);
                            setFeedbackStatus('negative');
                            setShowFeedbackDialog(true);
                        }}
                        className={`p-2 rounded-full transition-colors ${feedbackStatus === 'negative' ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100 text-gray-500'
                            }`}
                        aria-label="Thumbs down"
                    >
                        <ThumbsDown size={20} />
                    </button>
                </div>
            </div>

            {showFeedbackDialog && (
                <FeedbackDialog
                    onClose={() => setShowFeedbackDialog(false)}
                    onSubmit={handleFeedback}
                />
            )}
        </>
    );
};

export { SwimlaneTabs };